import api from './api';
import { SITE_ORIGIN } from '../utils/seoJsonLd';

// ============================================
// LandingPageService (2026-09-20) – öffentliche Feature-Landingpages
// unter /loesungen/<slug> (DE) bzw. /en/loesungen/<slug> (EN).
//   GET /api/landing-pages?lang=&audience=      → { pages: LandingPageSummary[] } (nur veröffentlicht)
//   GET /api/landing-page/slug/:slug?lang=      → { page, lang, availableLangs, locale, stats, related }
//   GET /api/landing-page/resolve/:slug         → { slug } (Alias → kanonischer Slug) oder 404
// Alle Antworten werden defensiv geparst: fehlende/kaputte Felder ergeben
// leere Werte, unbekannte Blocktypen fallen weg (Renderer ignoriert sie).
// ============================================

export type LandingLang = 'de' | 'en';
export const LANDING_LANGS: LandingLang[] = ['de', 'en'];

export type LandingAudience = 'club' | 'pub' | 'organizer' | 'shop' | 'player' | 'fan' | 'all';
export const LANDING_AUDIENCES: LandingAudience[] = ['club', 'pub', 'organizer', 'shop', 'player', 'fan', 'all'];

export type LandingCtaStyle = 'primary' | 'secondary';

export interface LandingCta {
  label: string;
  href: string;
  style: LandingCtaStyle;
}

export interface LandingFaqEntry {
  question: string;
  answer: string;
  order: number;
}

export interface LandingPageSummary {
  slug: string;
  audience: LandingAudience;
  heroImage: string | null;
  title: string;
  subtitle: string;
  sortOrder: number;
}

// ---------- Blöcke ----------

interface BlockBase {
  blockId: string;
  order: number;
}
export interface HeadingBlock extends BlockBase { type: 'heading'; text: string; level: 2 | 3 }
export interface ParagraphBlock extends BlockBase { type: 'paragraph'; text: string }
export interface ImageBlock extends BlockBase {
  type: 'image'; imageUrl: string; imageAlt: string; imageCaption: string; imageMaxWidth: number | null;
}
export interface GalleryImage { url: string; alt: string }
export interface GalleryBlock extends BlockBase { type: 'gallery'; images: GalleryImage[] }
export interface ListBlock extends BlockBase { type: 'list'; items: string[]; listType: 'bullet' | 'numbered' }
export interface TableBlock extends BlockBase { type: 'table'; headers: string[]; rows: string[][] }
export type CalloutVariant = 'info' | 'tip' | 'warning';
export interface CalloutBlock extends BlockBase { type: 'callout'; text: string; variant: CalloutVariant }
export interface DividerBlock extends BlockBase { type: 'divider' }
export interface FeatureItem { icon: string; title: string; text: string; href: string | null }
export interface FeatureGridBlock extends BlockBase { type: 'featureGrid'; title: string; items: FeatureItem[] }
export interface StepItem { title: string; text: string; imageUrl: string | null; imageAlt: string }
export interface StepsBlock extends BlockBase { type: 'steps'; title: string; items: StepItem[] }
export interface CtaBlock extends BlockBase { type: 'cta'; title: string; text: string; buttons: LandingCta[] }
export interface StatItem { key: string; label: string }
export interface StatsBlock extends BlockBase { type: 'stats'; items: StatItem[] }
export interface LogoItem { imageUrl: string; alt: string; href: string | null }
export interface LogosBlock extends BlockBase { type: 'logos'; items: LogoItem[] }
export interface VideoEmbedBlock extends BlockBase { type: 'videoEmbed'; url: string; caption: string; posterUrl: string | null }
export interface FaqItem { question: string; answer: string }
export interface FaqBlock extends BlockBase { type: 'faq'; items: FaqItem[] }
export interface RelatedPagesBlock extends BlockBase { type: 'relatedPages'; slugs: string[] }

/**
 * PURE (Marcel 14.10.2026, „Passende Lösungen“ doppelt auf /loesungen/turniersoftware): Enthält der
 * Seiteninhalt bereits einen relatedPages-Block mit mindestens einem auflösbaren Slug, darf die Seite
 * den automatischen Related-Block NICHT zusätzlich anhängen.
 */
export function hasRelatedPagesBlock(blocks: LandingBlock[] | null | undefined, relatedSlugs?: string[] | null): boolean {
  if (!Array.isArray(blocks)) return false;
  const pool = Array.isArray(relatedSlugs) ? new Set(relatedSlugs) : null;
  return blocks.some((b) => b?.type === 'relatedPages' && Array.isArray((b as RelatedPagesBlock).slugs)
    && (b as RelatedPagesBlock).slugs.some((slug) => typeof slug === 'string' && slug.length > 0 && (pool == null || pool.has(slug))));
}

export type LandingBlock =
  | HeadingBlock | ParagraphBlock | ImageBlock | GalleryBlock | ListBlock | TableBlock | CalloutBlock | DividerBlock
  | FeatureGridBlock | StepsBlock | CtaBlock | StatsBlock | LogosBlock | VideoEmbedBlock | FaqBlock | RelatedPagesBlock;

export type LandingBlockType = LandingBlock['type'];

export const LANDING_BLOCK_TYPES: LandingBlockType[] = [
  'heading', 'paragraph', 'image', 'gallery', 'list', 'table', 'callout', 'divider',
  'featureGrid', 'steps', 'cta', 'stats', 'logos', 'videoEmbed', 'faq', 'relatedPages',
];

// ---------- Seite ----------

export interface LandingLocale {
  title: string;
  subtitle: string;
  heroKicker: string;
  heroTitle: string;
  heroText: string;
  heroCtas: LandingCta[];
  blocks: LandingBlock[];
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string[];
  faq: LandingFaqEntry[];
}

export interface LandingPageMeta {
  slug: string;
  audience: LandingAudience;
  heroImage: string | null;
  relatedSlugs: string[];
  published: boolean;
  noindex: boolean;
  updatedAt: string | null;
}

export interface LandingRelated {
  slug: string;
  title: string;
  subtitle: string;
  heroImage: string | null;
}

export interface LandingPageDetail {
  page: LandingPageMeta;
  lang: LandingLang;
  availableLangs: LandingLang[];
  locale: LandingLocale;
  stats: Record<string, number>;
  related: LandingRelated[];
}

// ---------- pure Helfer ----------

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : fallback);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => str(x)).filter((x) => x.trim().length > 0) : []);
const list = (v: unknown): any[] => (Array.isArray(v) ? v : []);
const obj = (v: unknown): Record<string, any> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : {});

export const parseLang = (v: unknown): LandingLang | null => {
  if (typeof v !== 'string') return null;
  const code = v.trim().toLowerCase().split(/[-_]/)[0];
  return (LANDING_LANGS as string[]).includes(code) ? (code as LandingLang) : null;
};

export const parseAudience = (v: unknown): LandingAudience =>
  typeof v === 'string' && (LANDING_AUDIENCES as string[]).includes(v) ? (v as LandingAudience) : 'all';

export const parseCta = (c: unknown): LandingCta | null => {
  const o = obj(c);
  const label = str(o.label).trim();
  const href = str(o.href).trim();
  if (!label || !href) return null;
  return { label, href, style: o.style === 'secondary' ? 'secondary' : 'primary' };
};

const parseCtas = (v: unknown): LandingCta[] => list(v).map(parseCta).filter((c): c is LandingCta => c !== null);

export const parseFaqEntry = (f: unknown, index = 0): LandingFaqEntry | null => {
  const o = obj(f);
  const question = str(o.question).trim();
  const answer = str(o.answer).trim();
  if (!question || !answer) return null;
  return { question, answer, order: num(o.order, index) };
};

/** Einen Block defensiv parsen; unbekannte Typen → null (werden ignoriert). */
export const parseLandingBlock = (raw: unknown, index = 0): LandingBlock | null => {
  const b = obj(raw);
  const base: BlockBase = { blockId: str(b.blockId ?? b._id ?? b.id, `block-${index}`), order: num(b.order, index) };
  switch (b.type) {
    case 'heading':
      return { ...base, type: 'heading', text: str(b.text), level: b.level === 3 ? 3 : 2 };
    case 'paragraph':
      return { ...base, type: 'paragraph', text: str(b.text) };
    case 'image':
      return {
        ...base, type: 'image',
        imageUrl: str(b.imageUrl ?? b.url), imageAlt: str(b.imageAlt ?? b.alt), imageCaption: str(b.imageCaption ?? b.caption),
        imageMaxWidth: typeof b.imageMaxWidth === 'number' && b.imageMaxWidth > 0 ? b.imageMaxWidth : null,
      };
    case 'gallery':
      return {
        ...base, type: 'gallery',
        images: list(b.images)
          .map((img) => ({ url: str(obj(img).url ?? obj(img).imageUrl).trim(), alt: str(obj(img).alt ?? obj(img).caption) }))
          .filter((img) => img.url.length > 0),
      };
    case 'list':
      return { ...base, type: 'list', items: strList(b.items ?? b.listItems), listType: b.listType === 'numbered' ? 'numbered' : 'bullet' };
    case 'table':
      return {
        ...base, type: 'table',
        headers: list(b.headers ?? b.tableHeaders).map((h) => str(h)),
        rows: list(b.rows ?? b.tableRows).filter(Array.isArray).map((r: any[]) => r.map((c) => str(c))),
      };
    case 'callout':
      return {
        ...base, type: 'callout', text: str(b.text),
        variant: (['info', 'tip', 'warning'] as const).includes(b.variant ?? b.calloutVariant) ? (b.variant ?? b.calloutVariant) : 'info',
      };
    case 'divider':
      return { ...base, type: 'divider' };
    case 'featureGrid':
      return {
        ...base, type: 'featureGrid', title: str(b.title),
        items: list(b.items).map((it) => ({
          icon: str(obj(it).icon).trim(), title: str(obj(it).title), text: str(obj(it).text), href: strOrNull(obj(it).href),
        })).filter((it) => it.title.trim().length > 0 || it.text.trim().length > 0),
      };
    case 'steps':
      return {
        ...base, type: 'steps', title: str(b.title),
        items: list(b.items).map((it) => ({
          title: str(obj(it).title), text: str(obj(it).text), imageUrl: strOrNull(obj(it).imageUrl), imageAlt: str(obj(it).imageAlt),
        })).filter((it) => it.title.trim().length > 0 || it.text.trim().length > 0),
      };
    case 'cta':
      return { ...base, type: 'cta', title: str(b.title), text: str(b.text), buttons: parseCtas(b.buttons ?? b.ctas) };
    case 'stats':
      return {
        ...base, type: 'stats',
        items: list(b.items).map((it) => ({ key: str(obj(it).key).trim(), label: str(obj(it).label) })).filter((it) => it.key.length > 0),
      };
    case 'logos':
      return {
        ...base, type: 'logos',
        items: list(b.items).map((it) => ({
          imageUrl: str(obj(it).imageUrl ?? obj(it).url).trim(), alt: str(obj(it).alt), href: strOrNull(obj(it).href),
        })).filter((it) => it.imageUrl.length > 0),
      };
    case 'videoEmbed':
      return { ...base, type: 'videoEmbed', url: str(b.url).trim(), caption: str(b.caption), posterUrl: strOrNull(b.posterUrl) };
    case 'faq':
      return {
        ...base, type: 'faq',
        items: list(b.items).map((it) => ({ question: str(obj(it).question).trim(), answer: str(obj(it).answer).trim() }))
          .filter((it) => it.question.length > 0 && it.answer.length > 0),
      };
    case 'relatedPages':
      return { ...base, type: 'relatedPages', slugs: strList(b.slugs) };
    default:
      return null;
  }
};

export const parseLandingBlocks = (v: unknown): LandingBlock[] =>
  list(v)
    .map((b, i) => parseLandingBlock(b, i))
    .filter((b): b is LandingBlock => b !== null)
    .sort((a, b) => a.order - b.order);

export const parseLandingSummary = (d: unknown): LandingPageSummary | null => {
  const o = obj(d);
  const slug = str(o.slug).trim();
  if (!slug) return null;
  return {
    slug,
    audience: parseAudience(o.audience),
    heroImage: strOrNull(o.heroImage),
    title: str(o.title),
    subtitle: str(o.subtitle),
    sortOrder: num(o.sortOrder),
  };
};

export const parseLandingLocale = (d: unknown): LandingLocale => {
  const o = obj(d);
  return {
    title: str(o.title),
    subtitle: str(o.subtitle),
    heroKicker: str(o.heroKicker),
    heroTitle: str(o.heroTitle),
    heroText: str(o.heroText),
    heroCtas: parseCtas(o.heroCtas),
    blocks: parseLandingBlocks(o.blocks),
    seoTitle: str(o.seoTitle),
    seoDescription: str(o.seoDescription),
    seoKeywords: strList(o.seoKeywords),
    faq: list(o.faq)
      .map((f, i) => parseFaqEntry(f, i))
      .filter((f): f is LandingFaqEntry => f !== null)
      .sort((a, b) => a.order - b.order),
  };
};

export const parseLandingDetail = (d: unknown): LandingPageDetail | null => {
  const o = obj(d);
  const page = obj(o.page);
  const slug = str(page.slug).trim();
  if (!slug) return null;
  const stats: Record<string, number> = {};
  for (const [k, v] of Object.entries(obj(o.stats))) {
    if (typeof v === 'number' && Number.isFinite(v)) stats[k] = v;
  }
  const availableLangs = list(o.availableLangs).map(parseLang).filter((l): l is LandingLang => l !== null);
  const lang = parseLang(o.lang) ?? availableLangs[0] ?? 'de';
  return {
    page: {
      slug,
      audience: parseAudience(page.audience),
      heroImage: strOrNull(page.heroImage),
      relatedSlugs: strList(page.relatedSlugs),
      published: page.published !== false,
      noindex: page.noindex === true,
      updatedAt: strOrNull(page.updatedAt) ?? (typeof page.updatedAt === 'number' ? new Date(page.updatedAt).toISOString() : null),
    },
    lang,
    availableLangs: availableLangs.length > 0 ? Array.from(new Set(availableLangs)) : [lang],
    // Backend liefert locale innerhalb von page (alternativ top-level) – beides akzeptieren
    locale: parseLandingLocale(o.locale ?? page.locale),
    stats,
    related: list(o.related)
      .map((r) => {
        const s = parseLandingSummary(r);
        return s ? { slug: s.slug, title: s.title, subtitle: s.subtitle, heroImage: s.heroImage } : null;
      })
      .filter((r): r is LandingRelated => r !== null),
  };
};

/** Pfad einer Landingpage je Sprache: DE ohne, EN mit /en-Präfix. Ohne Slug → Hub. */
export const landingPath = (slug: string | null | undefined, lang: LandingLang = 'de'): string => {
  const base = lang === 'en' ? '/en/loesungen' : '/loesungen';
  const s = (slug ?? '').trim();
  return s ? `${base}/${s}` : base;
};

/** Zahl aus dem stats-Objekt (null, wenn unbekannt/keine endliche Zahl). */
export const resolveStat = (stats: Record<string, unknown> | null | undefined, key: string): number | null => {
  if (!stats || typeof stats !== 'object' || !key) return null;
  const v = (stats as Record<string, unknown>)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/** Zahl für die Anzeige (Tausenderpunkt je Sprache), null → „–“. */
export const formatStat = (value: number | null, lang: LandingLang = 'de'): string =>
  value === null ? '–' : value.toLocaleString(lang === 'en' ? 'en-GB' : 'de-DE');

/** Slug-artige ID aus Text (für Sprungmarken); leer → null. */
export const slugifyAnchor = (text: string): string | null => {
  const s = (text ?? '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return s || null;
};

/** Sprungmarken-ID eines Blocks: Überschrift/Titel als Slug, sonst Blocktyp + Index. */
export const blockAnchorId = (block: LandingBlock, index: number): string => {
  const text =
    block.type === 'heading' ? block.text
    : block.type === 'featureGrid' || block.type === 'steps' || block.type === 'cta' ? block.title
    : '';
  return slugifyAnchor(text) ?? `${block.type.toLowerCase()}-${index + 1}`;
};

export type CtaTarget =
  | { kind: 'internal'; to: string }
  | { kind: 'anchor'; href: string }
  | { kind: 'external'; href: string; newTab: boolean };

/**
 * Link-Ziel eines CTA: relative Pfade bleiben SPA-Links (auch absolute
 * dartcloudapp.de-URLs), https/http extern in neuem Tab, mailto/tel extern
 * im selben Tab, #anker als Sprungmarke.
 */
export const ctaHref = (href: string | null | undefined): CtaTarget => {
  const h = (href ?? '').trim();
  if (!h) return { kind: 'internal', to: '/' };
  if (h.startsWith('#')) return { kind: 'anchor', href: h };
  if (h.startsWith(SITE_ORIGIN + '/') || h === SITE_ORIGIN) return { kind: 'internal', to: h.slice(SITE_ORIGIN.length) || '/' };
  if (/^https?:\/\//i.test(h)) return { kind: 'external', href: h, newTab: true };
  if (/^(mailto|tel):/i.test(h)) return { kind: 'external', href: h, newTab: false };
  if (h.startsWith('/')) return { kind: 'internal', to: h };
  return { kind: 'internal', to: `/${h}` };
};

/** Bild-URL aus Dateiname/Pfad; optional Backend-Thumbnail (/api/file/<name>/thumbnail/<size>). */
export const buildFileUrl = (name: string | null | undefined, thumbSize?: number): string | null => {
  if (!name || !name.trim()) return null;
  const u = name.trim();
  if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u;
  const base = api.defaults.baseURL || '';
  if (u.startsWith('/')) return `${base}${u}`;
  const file = encodeURIComponent(u);
  return thumbSize ? `${base}/api/file/${file}/thumbnail/${thumbSize}` : `${base}/api/file/${file}`;
};

export type VideoSource =
  | { kind: 'iframe'; src: string }
  | { kind: 'file'; src: string }
  | { kind: 'link'; href: string };

/**
 * Video-Quelle für den videoEmbed-Block (pure): YouTube/Vimeo → Embed-URL
 * (nocookie, autoplay nach Klick), .mp4/.webm → natives <video>, sonst
 * externer Link. Es wird NIE vor dem Klick geladen (nur Poster).
 */
export const videoEmbedSource = (url: string | null | undefined): VideoSource | null => {
  const u = (url ?? '').trim();
  if (!u) return null;
  const yt = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0` };
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1` };
  if (/\.(mp4|webm|ogv)(\?.*)?$/i.test(u)) return { kind: 'file', src: buildFileUrl(u) ?? u };
  if (/^https?:\/\//i.test(u)) return { kind: 'link', href: u };
  return null;
};

/** Alle FAQ-Einträge für FAQPage-JSON-LD: page.faq, sonst FAQ-Blöcke. */
export const collectFaq = (locale: LandingLocale): FaqItem[] => {
  if (locale.faq.length > 0) return locale.faq.map((f) => ({ question: f.question, answer: f.answer }));
  return locale.blocks.flatMap((b) => (b.type === 'faq' ? b.items : []));
};

// ---------- API ----------

class LandingPageService {
  /** Veröffentlichte Seiten (Hub/Navigation). Fehler → []. */
  async getPages(lang: LandingLang = 'de', audience?: LandingAudience | null): Promise<LandingPageSummary[]> {
    try {
      const params: Record<string, string> = { lang };
      if (audience) params.audience = audience;
      const res = await api.get('/api/landing-pages', { params });
      const raw = list(res?.data?.pages ?? res?.data?.data ?? res?.data);
      return raw
        .map(parseLandingSummary)
        .filter((p): p is LandingPageSummary => p !== null)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
    } catch {
      return [];
    }
  }

  /** Seite per Slug und Sprache. null bei 404/unbekannt. */
  async getBySlug(slug: string, lang: LandingLang = 'de'): Promise<LandingPageDetail | null> {
    try {
      const res = await api.get(`/api/landing-page/slug/${encodeURIComponent(slug)}`, { params: { lang } });
      return parseLandingDetail(res?.data?.data ?? res?.data);
    } catch {
      return null;
    }
  }

  /** Alias → kanonischer Slug; null wenn unbekannt. */
  async resolveSlug(slug: string): Promise<string | null> {
    try {
      const res = await api.get(`/api/landing-page/resolve/${encodeURIComponent(slug)}`);
      const resolved = str(res?.data?.slug ?? res?.data?.data?.slug).trim();
      return resolved || null;
    } catch {
      return null;
    }
  }

  buildFileUrl = buildFileUrl;
}

const landingPageService = new LandingPageService();
export default landingPageService;
